import React from 'react';
import CheckIcon from './CustomIcons/CheckIcon';
import StepCurrentIcon from './CustomIcons/StepCurrentIcon';
import StepUpcomingIcon from './CustomIcons/StepUpcomingIcon';
import { FOCUS_RING } from './focusRing';

export interface StepTabItem {
    key: string;
    label: string;
    /** Position marker shown at the trailing edge. Defaults to `Step ${index + 1}`. */
    hint?: string;
    disabled?: boolean;
}

export interface StepTabsProps {
    items: StepTabItem[];
    activeKey: string;
    /** Steps already satisfied. They render a check and stay reachable, so a user can go back. */
    completedKeys?: string[];
    onChange: (key: string) => void;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
}

type StepState = 'current' | 'complete' | 'upcoming';

const STATE_CLASSES: Record<StepState, string> = {
    current:
        'shadow-default border-[var(--malachite-card-border)] bg-[var(--malachite-card-background)] text-[var(--malachite-text-primary)]',
    complete:
        'border-transparent bg-[var(--malachite-success-background-glass)] text-[var(--malachite-text-success)]',
    upcoming:
        'border-transparent bg-[var(--malachite-card-dimmed-background)] text-[var(--malachite-text-secondary)]',
};

const StepIcon: React.FC<{ state: StepState }> = ({ state }) => {
    if (state === 'complete') {
        return <CheckIcon size={14} />;
    }
    if (state === 'current') {
        return <StepCurrentIcon size={14} />;
    }
    return <StepUpcomingIcon size={14} />;
};

/**
 * A horizontal step navigator whose steps are destinations, unlike the read-only `Stepper`.
 * Each step is a button with `aria-current="step"`, not a tab.
 */
export const StepTabs: React.FC<StepTabsProps> = ({
    items,
    activeKey,
    completedKeys = [],
    onChange,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
}) => {
    const completed = new Set(completedKeys);

    return (
        <ol
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            className={`m-0 flex list-none gap-2 p-0 ${className}`.trim()}
        >
            {items.map((item, index) => {
                const state: StepState =
                    item.key === activeKey
                        ? 'current'
                        : completed.has(item.key)
                          ? 'complete'
                          : 'upcoming';

                return (
                    <li key={item.key} className="min-w-0 flex-1">
                        <button
                            type="button"
                            disabled={item.disabled}
                            aria-current={state === 'current' ? 'step' : undefined}
                            onClick={() => onChange(item.key)}
                            className={`${FOCUS_RING} flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-lg border px-3 py-2.5 text-left text-[13px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${STATE_CLASSES[state]}`}
                        >
                            <span aria-hidden="true" className="flex shrink-0 items-center">
                                <StepIcon state={state} />
                            </span>
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                            {/* The state is otherwise carried only by colour and an aria-hidden icon. */}
                            {state === 'complete' && <span className="sr-only">Completed</span>}
                            <span className="shrink-0 font-normal text-[var(--malachite-text-secondary)]">
                                {item.hint ?? `Step ${index + 1}`}
                            </span>
                        </button>
                    </li>
                );
            })}
        </ol>
    );
};

export default StepTabs;
